import { FC, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { AuthContext } from "../../../contexts/AuthContext";
import Loader from "../../../components/Loader";
import Text from "../../../components/Text";
import Divider from "../../../components/Divider";
import Modal from "../../../components/Modal";
import ModalHeader from "../../../components/Modal/ModalHeader";
import { fetchMemberDetailsUser } from "../../../services/api/admin/user";
import { Question } from "../../../types/Question";
import Notes from "./Notes";
import Answers from "./Anwsers";
import { anonymizeEmail, getUserName } from "../../../utils/string";
import Tags from "./Tags";

import styles from "./styles.module.scss";

export type MemberDetailsUser = {
    id: string;
    firstname?: string | null;
    lastname?: string | null;
    pseudo?: string | null;
    email?: string | null;
    proSince?: Date | string | null;
    questions?: Question[];
};

interface Props {
    open: boolean;
    handleClose: () => void;
    user?: MemberDetailsUser;
}

const MemberDetails: FC<Props> = ({ open, handleClose, user }) => {
    const { hasResource, hasPermission, isStreamOn } = useContext(AuthContext)!;
    const needsProfile = open && !!user?.id && user.questions === undefined;
    const [profile, setProfile] = useState<MemberDetailsUser | undefined>();
    const [loadingProfile, setLoadingProfile] = useState(false);

    useEffect(() => {
        if (!needsProfile || !user?.id) {
            setProfile(undefined);
            setLoadingProfile(false);
            return;
        }

        let cancelled = false;
        setProfile(undefined);
        setLoadingProfile(true);
        fetchMemberDetailsUser(user.id)
            .then((full) => {
                if (!cancelled) setProfile(full ?? undefined);
            })
            .catch(() => {
                if (!cancelled) {
                    toast.error("Impossible de charger les informations du membre.");
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingProfile(false);
            });

        return () => {
            cancelled = true;
        };
    }, [needsProfile, user?.id]);

    const displayed = needsProfile
        ? profile ?? (loadingProfile ? undefined : user)
        : user;
    const proSinceRaw = displayed?.proSince ?? null;
    const proSinceDate = proSinceRaw ? new Date(proSinceRaw) : null;
    const proSinceLabel =
        proSinceDate && !Number.isNaN(proSinceDate.getTime())
            ? `Pro depuis ${proSinceDate.toLocaleDateString("fr-FR")}`
            : undefined;

    return (
        <Modal open={open} onClose={handleClose} size="medium">
            <div className={styles.container}>
                {!displayed ? (
                    <Loader />
                ) : (
                    <>
                        <div>
                            <ModalHeader
                                title={getUserName(
                                    displayed.firstname ?? "",
                                    displayed.lastname ?? "",
                                    displayed.pseudo ?? "",
                                    isStreamOn
                                )}
                                titleRightLabel={proSinceLabel}
                                handleClose={handleClose}
                            />
                            <div className={styles.body}>
                                <Text variant="body">
                                    {isStreamOn
                                        ? anonymizeEmail(displayed.email ?? "")
                                        : displayed.email}
                                </Text>
                            </div>
                        </div>
                        {hasPermission("admin-members", "read") && (
                            <Tags user={displayed} />
                        )}
                        {displayed.questions && displayed.questions.length > 0 && (
                            <>
                                <Divider className={styles.divider} />
                                <Answers user={{ questions: displayed.questions }} />
                            </>
                        )}
                        {(hasResource("user-notes") ||
                            hasResource("user-notes-admin")) && (
                            <>
                                <Divider className={styles.divider} />
                                <Notes user={displayed} />
                            </>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default MemberDetails;
